import {Component, EventEmitter, Input, Output} from '@angular/core';
import {StorageManager} from '../../../storage-manager.class';

import type { OnInit } from '@angular/core';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { Form, FormType } from '../interfaces/form.interface';
import { FormGroup } from '@angular/forms';
import { EventPhase, FormPhase, FormRequest } from '../form-request';
import { FormOperations } from '../utils/form-operations';


@Component({
    selector: 'app-reactive-request-button',
    standalone: false,
    templateUrl: './request-button.component.html',
    styleUrls: ['./request-button.component.scss']
})


export class ReactiveRequestButtonComponent<T> extends FormRequest implements OnInit {

    @Input() form!: Form<T>;
    @Input() model!: FormGroup;
    @Input() recordHasAssociatedData = false;
    @Input() request = new RequestHandler<any>();
    @Input() getRequest = new RequestHandler<any>();
    @Input() lockOnSuccess = false;
    @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
    @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();

    public validationError: string | null = null;
    public warning: string | null = null;
    
    public requestError: string[] = [];
    public REQUEST_BUTTON_TYPES = FormType;

    private formOperations = new FormOperations(this.form, this.model);
    
    constructor() {
        super();
    }

    /**
     *
     * @returns form submission info.
     */
    get buttonLabel(): string {
        if (this.validationError) {
            return this.validationError;
        }// if();
    
        if (this.warning) {
            return this.warning;
        }// if();
    
        if (this.request.hasError) {
            return this.request.hasErrorCliExists ? 'Cliente ya existe...' : this.requestError[0] ?? '';
        }// if();
        
        const stateLabels: Record<FormType, [string, string, string]> = {
            [this.REQUEST_BUTTON_TYPES.CREATION]: ['Guardar', 'Guardando...', 'Guardado'],
            [this.REQUEST_BUTTON_TYPES.EDITION]: ['Actualizar', 'Actualizando...', 'Actualizado'],
            [this.REQUEST_BUTTON_TYPES.DUPLICATION]: ['Duplicar', 'Duplicando...', 'Duplicado'],
            [this.REQUEST_BUTTON_TYPES.DELETION]: ['Eliminar', 'Eliminando...', 'Eliminado'],
            [this.REQUEST_BUTTON_TYPES.INFORMATIVE]: ['', '', ''],
            [this.REQUEST_BUTTON_TYPES.EXPORT]: ['', '', ''],
            [this.REQUEST_BUTTON_TYPES.GENERATION]: ['Generar Informe', 'Generando Informe...', 'Informe Generado']
        };
    
        const [defaultLabel, loadingLabel, doneLabel] = stateLabels[this.form.type];
    
        return this.request.hasError === undefined ? defaultLabel :
            this.request.isLoading ? loadingLabel :
                doneLabel;
    }// ();

    /**
     * Sets generic errors for requests
     */
    ngOnInit() {
        const isDemo = (StorageManager.getUser() || {}).rol === 'demo';

        this.requestError[0] = isDemo ? 'Operación no permitida' : 'Ha ocurrido un error...';
        this.requestError[1] = isDemo ? '' : 'Volver a intentar';
    }// ();


    /**
     * Evaluates the validity of form data in order to perform the most appropiate action.
     */
    public submit(): void {
        // 1) Limpiamos cualquier mensaje de validación previo
        this.validationError = null;

        // 2) Si sigue sin ser válido, mostramos errores y salimos
        if (!this.model.valid && this.form.type !== FormType.DELETION) {
            this.displayErrorsOnButton();
            return;
        }

        if (this.recordHasAssociatedData && !this.warning) {
            this.warning = 'El registro tiene datos, por lo que se borrará toda la información asociada. <br> ¿Continuar?';
            return;
        }

        // 3) Si es válido, construimos el payload y enviamos
        const payload = this.updateObjectWithIds(this.model.getRawValue());
        if (this.form.type === FormType.DUPLICATION) {
            delete (payload as any)['id'];
        }
        
        this.sendRequest(payload as T);
    }


    /**
     * Replaces the objects that are part of the form submission with their id.
     * @param data A JSON with the data as submitted by the user
     * @returns A JSON with ids instead of the full object
     */
    private updateObjectWithIds(data: Record<string, any>): Record<string, unknown> {
        const fieldMap = new Map<string, string>();

        // Recorrem totes les seccions i els seus camps per crear un map de claus -> primaryKey
        for (const section of this.form.sections ?? []) {
            for (const field of section.fields ?? []) {
                const primaryKey = field.input.dropdownConfiguration?.valuePrimaryKey;
                if (primaryKey) {
                    fieldMap.set(field.name, primaryKey);
                }
            }
        }

        return Object.entries(data).reduce((result, [key, value]) => {
            const primaryKey = fieldMap.get(key) || 'id';

            if (Array.isArray(value)) {
                result[key] = value
                    .map(item => item?.[primaryKey])
                    .filter(Boolean)
                    .join(';');
            } else if (value && typeof value === 'object' && value[primaryKey]) {
                result[key] = value[primaryKey];
            } else {
                result[key] = value;
            }

            return result;
        }, {} as Record<string, unknown>);
    }

    

    /**
     * Performs the request, returning the user to the previous location on success.
     * @param payload A JSON used as the payload of a POST/PUT request
     */
    private sendRequest(payload: T): void {
        this.executePhase(FormPhase.SEND);
        this.addEvent(FormPhase.SEND, EventPhase.ON, (resolve) => {
            this.request.safePerform(payload);
            this.request.response((res) => {
                this.getRequest.safePerform();
                this.afterSend.emit(res);
                resolve(true);
            });
        });

        this.addEvent(FormPhase.FINISH, EventPhase.ON, (resolve) => {
            setTimeout(() => this.request.unsuscribe(), 1000);
            this.returnToMainView();
            resolve(true);
        });
    }// ();

    /**
     * Returns the user to the previous location. 
     */
    private returnToMainView(): void {
        this.goBack.emit();
    }// ();

    /**
     * Displays on the button errors related with invalid fields.
     */
    private displayErrorsOnButton() {
        const errorMessages: Record<string, (errors: Record<string,string>) => string> = {
            required: () => 'es obligatorio.',
            maxlength: () => 'ha excedido la longitud máxima.',
            email: () => 'no es un correo válido.',
            invalidTime: (errors) => errors['invalidTime'] ?? '',
            invalidSpanishId: (errors) => errors['invalidSpanishId'] ?? '',
            spanishIdTaken: (errors) => errors['spanishIdTaken'] ?? '',
        };
    
        const invalidFields = Object.entries(this.model.controls).reduce<string[]>((acc, [field, control]) => {
            if (!control?.invalid) {
                return acc;
            }

            this.formOperations = new FormOperations(this.form, this.model);

            const fieldInfo = this.formOperations.getFieldByControlName(field);
            const errors = control?.errors ?? {};

            const messages = Object.keys(errors)
                .filter(key => key in errorMessages)
                .map(key => errorMessages[key]?.(errors))
                .join(' ');
    
            acc.push(`${fieldInfo?.label} ${messages || ': Error desconocido.'}`);
            return acc;
        }, []);
    
        this.validationError = `El formulario no es válido. Revisa los campos:<br>- ${invalidFields.join('<br>- ')}`;
        this.model.markAllAsTouched();
    }// ();
}// class;
